import { isEmpty } from 'lodash-es';

import { ISmallBiddersBySiteParams } from '~/src/dash/api/filters/bidders';
import { encodeForm } from '~/src/dash/api/resource';
import { createUrl, restPost } from '~/src/dash/api/rest';
import { EBool } from '~/src/dash/api/types/bool';
import { ISessionState } from '~/src/dash/reducers/session-reducers';
import { IUserState } from '~/src/dash/reducers/user-reducers';
import { IListItem, IMenuList } from '~/src/dash/types/menuList';
import { getHeaderBidderAdaptersForPublisher } from '~/src/dash/utils/header-bidder-adapters';
import { hasRoleSuperUser } from '~/src/dash/utils/roles';
import { ISelectOption } from '~/src/dash/components/dropdowns/common';

export interface IFilteredDemandSources {
  DemandSourcesMap: Record<string, boolean>;
}

export async function queryFilteredDemandSources(
  publisherID: string,
  query: ISmallBiddersBySiteParams,
  onlyActive?: boolean, // true gets only active filtered demand sources, false gets all filtered demand sources (inactive and active)
): Promise<IFilteredDemandSources> {
  let reqUrl = `${createUrl('/filtered_demand_sources', publisherID, 'v2')}`;
  if (onlyActive) {
    reqUrl += '&active=' + encodeURIComponent(String(EBool.EBool_TRUE));
  }
  const headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
  const body = encodeForm(query, headers['Content-Type']);
  const response = await restPost<any>(reqUrl, query, { method: 'post', body, headers });
  return response.parsedBody;
}

export const getFilteredDemandSourcesList = (
  session: ISessionState,
  user: IUserState,
  siteIDs?: string, // example: '30, 11, 23'
): Promise<Record<string, IMenuList>> => {
  const publisherID = session.publisher.ID;
  return queryFilteredDemandSources(publisherID, {
    publisher_id: publisherID,
    site_ids: siteIDs || '',
  })
    .then(({ DemandSourcesMap }) => {
      const demandSourceAdapters = [
        ...getHeaderBidderAdaptersForPublisher(
          '',
          false,
          session.publisher,
          hasRoleSuperUser(user.user),
        ),
      ];
      const demandSourcesList = {} as Record<string, IListItem>;

      // Publisher ID 1 gets all demand sources without filtering
      if (publisherID === '1') {
        demandSourceAdapters.forEach((adapter) => {
          demandSourcesList[adapter.key] = {
            ID: adapter.key,
            title: adapter.text,
            selected: false,
          };
        });
      } else {
        demandSourceAdapters.forEach((adapter) => {
          if (DemandSourcesMap[adapter.key]) {
            demandSourcesList[adapter.key] = {
              ID: adapter.key,
              title: adapter.text,
              selected: false,
            };
          }
        });
      }

      return { 'Filtered Demand Sources': demandSourcesList };
    })
    .catch((err) => {
      console.error(err);
      return { 'Filtered Demand Sources': {} };
    });
};

export interface IDemandSource {
  ID: string;
  Bidder: string;
  UIBidderType: string;
  Alias: string;
  BidderIDs: number[];
  IsCustom: boolean;
}

export interface IFilteredDemandSourcesWithBidderTypes {
  DemandSources: Record<string, IDemandSource>;
  CustomDemandSources: IDemandSource[];
}

export async function queryFilteredDemandSourcesWithBidderTypes(
  publisherID: string,
  query: ISmallBiddersBySiteParams,
): Promise<IFilteredDemandSourcesWithBidderTypes> {
  const reqUrl = `${createUrl('/filtered_demand_sources_with_bidder_types', publisherID, 'v2')}`;
  const headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
  const body = encodeForm(query, headers['Content-Type']);
  const response = await restPost<any>(reqUrl, query, { method: 'post', body, headers });
  return response.parsedBody;
}

export const getFilteredDemandSourcesWithBidderTypes = (
  session: ISessionState,
  user: IUserState,
  siteIDs?: string, // example: '30,11,23'
  isBidderTypeDisabled?: boolean,
): Promise<Record<string, IMenuList>> => {
  const publisherID = session.publisher.ID;
  return queryFilteredDemandSourcesWithBidderTypes(publisherID, {
    publisher_id: publisherID,
    site_ids: siteIDs || '',
  })
    .then(({ DemandSources, CustomDemandSources }) => {
      const demandSourceAdapters = [
        ...getHeaderBidderAdaptersForPublisher(
          '',
          false,
          session.publisher,
          hasRoleSuperUser(user.user),
          true,
        ),
      ];
      const demandSourcesList = {} as Record<string, IListItem>;
      if (!isEmpty(DemandSources)) {
        demandSourceAdapters.forEach((adapter) => {
          if (DemandSources[adapter.key]) {
            demandSourcesList[adapter.key] = {
              ID: adapter.key,
              title: adapter.text,
              selected: false,
              bidderIDs: DemandSources[adapter.key].BidderIDs,
            };
          }
        });
      }
      if (!isEmpty(CustomDemandSources)) {
        CustomDemandSources.forEach((cds) => {
          demandSourcesList[cds.ID] = {
            ID: cds.ID,
            title: cds.Alias,
            selected: false,
            bidderIDs: cds.BidderIDs,
            isCustom: cds.IsCustom,
            isDisabled: !!isBidderTypeDisabled,
          };
        });
      }
      return { 'Filtered Demand Sources': demandSourcesList };
    })
    .catch((err) => {
      console.error(err);
      return { 'Filtered Demand Sources': {} };
    });
};

export const generateDemandSourcesOptionsList = (
  session: ISessionState,
  user: IUserState,
): Promise<ISelectOption[]> => {
  const publisherID = session.publisher.ID;
  return queryFilteredDemandSourcesWithBidderTypes(publisherID, {
    publisher_id: publisherID,
    site_ids: '',
  })
    .then(({ DemandSources }) => {
      const demandSourceAdapters = [
        ...getHeaderBidderAdaptersForPublisher(
          '',
          false,
          session.publisher,
          hasRoleSuperUser(user.user),
          true,
        ),
      ];
      const options: ISelectOption[] = [];
      if (!isEmpty(DemandSources)) {
        demandSourceAdapters.forEach((adapter) => {
          options.push({
            id: adapter.key,
            label: adapter.text,
          });
        });
      }
      return options;
    })
    .catch((err) => {
      console.error(err);
      return [] as ISelectOption[];
    });
};
